import { APIPaths, SpotlightApi } from "@unit/apis";
import { useAtom } from "jotai";
import { useCallback, useMemo } from "react";

import { useAppSnackbar } from "@/custom-hooks/use-app-snackbar";
import { API_URL } from "@/global-state/firebase-settings";
import { idTokenAtom, loadingAtom } from "@/global-state/jotai-atom";

export const useSpotlightApi = () => {
  const { setAppSnackbar } = useAppSnackbar();
  const [, setLoading] = useAtom(loadingAtom);
  const [idToken] = useAtom(idTokenAtom);
  const spotlightApi = useMemo(() => new SpotlightApi(API_URL, idToken), [idToken]);

  const fetchSpotlightList = useCallback(
    async (
      parameters: APIPaths["/app/spotlight/list"]["get"]["parameters"]["query"],
      opts?: {
        ignoreLoading?: boolean;
      },
    ) => {
      if (!opts?.ignoreLoading) await setLoading(true);
      try {
        return await spotlightApi.getSpotlightList(parameters);
      } catch (e: any) {
        const errorMessage = e?.response?.data?.message || e?.response?.data?.devMessage || "error";
        setAppSnackbar(errorMessage, { error: true });
      } finally {
        await setLoading(false);
      }
    },
    [spotlightApi],
  );

  const fetchSpotlight = useCallback(
    async (id: APIPaths["/app/spotlight/{id}"]["get"]["parameters"]["path"]["id"]) => {
      await setLoading(true);
      try {
        return await spotlightApi.getSpotlightById(id);
      } catch (e: any) {
        const errorMessage = e?.response?.data?.message || e?.response?.data?.devMessage || "error";
        setAppSnackbar(errorMessage, { error: true });
      } finally {
        await setLoading(false);
      }
    },
    [spotlightApi],
  );

  return { fetchSpotlightList, fetchSpotlight };
};
